import React from 'react'
import { motion } from 'framer-motion'
import Navbar from '../components/navbar/navbar'
import Footer from '../components/footer'
import aboutImg from '../assets/img/sb.png'
import aboutImg2 from '../assets/img/vec-2.png'

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
}

const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
}

export default function AboutUs() {
    return (
        <div className="bg-white min-h-screen">
            <Navbar transparent={false} />

            {/* Hero Section */}
            <header className="relative py-24 bg-primary-dark overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent rounded-full blur-[120px] -mr-64 -mt-64"></div>
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent rounded-full blur-[120px] -ml-64 -mb-64"></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div 
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                        className="max-w-3xl"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-[2px] bg-accent"></div>
                            <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em]">Establishment 2026</span>
                        </div>
                        <h1 className="text-5xl md:text-6xl font-black text-white leading-tight mb-6">
                            My Property <span className="text-accent italic">Guru</span>
                        </h1>
                        <p className="text-white/70 text-xl font-medium leading-relaxed italic">
                            A New Era in Real Estate – Simple, Transparent, and Stress-Free.
                        </p>
                    </motion.div>
                </div>
            </header>

            {/* Our Story Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap items-center -mx-4">
                        <div className="lg:w-1/2 px-4 mb-12 lg:mb-0">
                            <motion.div 
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="relative"
                            >
                                <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent opacity-10 rounded-full blur-3xl"></div>
                                <img src={aboutImg} className="rounded-3xl shadow-2xl relative z-10 w-full object-cover aspect-[4/3]" alt="Our Story" />
                                <div className="absolute -bottom-8 -right-8 bg-white p-8 rounded-3xl shadow-xl z-20 hidden md:block border border-gray-50">
                                    <h3 className="text-4xl font-black text-primary-dark mb-1">2026</h3>
                                    <p className="text-accent font-bold text-xs uppercase tracking-widest">Founding Year</p>
                                </div>
                            </motion.div>
                        </div>
                        <div className="lg:w-1/2 px-4">
                            <motion.div 
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="lg:pl-16"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-accent font-black text-[10px] uppercase tracking-[0.3em]">Our Foundation</span>
                                </div>
                                <h2 className="text-4xl font-black text-black mb-8 tracking-tight leading-tight">
                                    Our <span className="text-accent italic">Story</span>
                                </h2>
                                <div className="space-y-6">
                                    <p className="text-black text-lg font-medium leading-relaxed">
                                        Founded in 2026, My Property Guru was created with a clear vision — to make property buying, selling, and renting simple, transparent, and stress-free.
                                    </p>
                                    <p className="text-black/70 leading-relaxed font-medium">
                                        We are a modern real estate consultancy built on trust, professionalism, and smart market strategies. Our goal is not just to close deals, but to build long-term relationships based on honesty and results.
                                    </p>
                                    <p className="text-black/70 leading-relaxed font-medium">
                                        As a new-age real estate brand, we combine traditional property expertise with digital innovation to deliver premium experiences for our clients.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
                                    {[
                                        "Modern & Transparent Platform",
                                        "Verified & Trusted Listings",
                                        "Personalized Client Support",
                                        "Premium Properties"
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                                                <i className="fa-solid fa-check text-accent text-[10px]"></i>
                                            </div>
                                            <span className="text-black font-bold text-sm tracking-tight">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision Section */}
            <section className="py-24 bg-gray-50 relative overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="flex flex-wrap -mx-6">
                        {/* Mission */}
                        <div className="lg:w-1/2 px-6 mb-12 lg:mb-0">
                            <motion.div 
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="bg-white p-12 rounded-3xl shadow-sm border border-gray-100 h-full hover:shadow-xl transition-all duration-500 group"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                                    <i className="fa-solid fa-bullseye text-accent text-3xl"></i>
                                </div>
                                <h3 className="text-3xl font-black text-black mb-6 uppercase tracking-tight">Our <span className="text-accent italic">Mission</span></h3>
                                <p className="text-black text-lg font-medium leading-relaxed mb-10">
                                    To simplify real estate decisions by providing honest guidance, verified properties, and complete transaction support — ensuring every client makes a confident investment.
                                </p>
                                <div className="space-y-4 pt-10 border-t border-gray-50">
                                    <p className="text-black font-black text-xs uppercase tracking-widest mb-6">We Focus On:</p>
                                    {[
                                        "Providing verified property options",
                                        "Ensuring transparent pricing",
                                        "Smooth and timely deal closures",
                                        "Building long-term client trust"
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-4">
                                            <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0"></span>
                                            <span className="text-black font-bold text-sm">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                        {/* Vision */}
                        <div className="lg:w-1/2 px-6">
                            <motion.div 
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="bg-white p-12 rounded-3xl shadow-sm border border-gray-100 h-full hover:shadow-xl transition-all duration-500 group"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                                    <i className="fa-solid fa-binoculars text-accent text-3xl"></i>
                                </div>
                                <h3 className="text-3xl font-black text-black mb-6 uppercase tracking-tight">Our <span className="text-accent italic">Vision</span></h3>
                                <p className="text-black text-lg font-medium leading-relaxed mb-10">
                                    To become one of India’s most trusted and respected real estate advisory brands, known for integrity, premium service, and customer satisfaction.
                                </p>
                                <div className="space-y-4 pt-10 border-t border-gray-50">
                                    <p className="text-black font-black text-xs uppercase tracking-widest mb-6">Our Future Goals:</p>
                                    {[
                                        "Expand across major Indian cities",
                                        "Strong network of developers & investors",
                                        "Digital property search solutions",
                                        "Highest standards of transparency"
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-4">
                                            <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0"></span>
                                            <span className="text-black font-bold text-sm">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Do Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                        <div className="lg:w-7/12">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-[2px] bg-accent"></div>
                                <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em]">Our Services</span>
                            </div>
                            <h2 className="text-4xl font-black text-black tracking-tight mb-4">Complete <span className="text-accent italic">Real Estate</span> Solutions</h2>
                            <p className="text-black text-lg font-medium max-w-2xl leading-relaxed">We offer end-to-end property consultancy services designed for buyers, sellers, investors, and landlords.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                title: "Buy Property",
                                icon: "fa-solid fa-house-chimney",
                                list: ["Residential properties", "Luxury villas", "Commercial spaces", "Investment advisory"]
                            },
                            {
                                title: "Sell Property",
                                icon: "fa-solid fa-hand-holding-dollar",
                                list: ["Property valuation", "Network access", "Professional marketing", "Documentation assistance"]
                            },
                            {
                                title: "Rent Property",
                                icon: "fa-solid fa-key",
                                list: ["Tenant verification", "Agreement coordination", "Corporate leasing", "Residential rentals"]
                            },
                            {
                                title: "Property Advisory",
                                icon: "fa-solid fa-chart-line",
                                list: ["Market analysis", "ROI planning", "Project comparison", "Portfolio advisory"]
                            }
                        ].map((item, i) => (
                            <motion.div 
                                key={i}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="bg-gray-50/50 p-8 rounded-3xl border border-gray-100 hover:bg-white hover:shadow-2xl hover:shadow-black/5 transition-all duration-500 group"
                            >
                                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-8 group-hover:bg-primary transition-all duration-500">
                                    <i className={`${item.icon} text-xl text-primary group-hover:text-white transition-all duration-500`}></i>
                                </div>
                                <h4 className="text-xl font-black text-black mb-6 uppercase tracking-tight">{item.title}</h4>
                                <ul className="space-y-3">
                                    {item.list.map((li, j) => (
                                        <li key={j} className="text-black/60 text-sm font-bold flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-accent opacity-50"></span>
                                            {li}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-24 bg-primary-dark relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent opacity-5 rounded-full blur-[150px] -mr-96 -mt-96"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-3xl mx-auto mb-20 text-white">
                        <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em] mb-4 block">Our Values</span>
                        <h2 className="text-4xl font-black tracking-tight mb-6 text-white">Why Choose <span className="text-accent italic">My Property Guru</span></h2>
                        <p className="text-white/80 text-lg font-medium leading-relaxed">Even as a newly launched brand, we are driven by strong values and a professional approach.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        {[
                            { title: "Transparent Process", desc: "No hidden charges. Clear documentation.", icon: "fa-solid fa-eye" },
                            { title: "Verified Listings", desc: "Every property is carefully screened.", icon: "fa-solid fa-circle-check" },
                            { title: "Professional Support", desc: "Dedicated assistance from inquiry to final deal.", icon: "fa-solid fa-user-tie" },
                            { title: "Premium Network", desc: "Strong connections with leading developers.", icon: "fa-solid fa-handshake" }
                        ].map((item, i) => (
                            <motion.div 
                                key={i}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="text-center text-white"
                            >
                                <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-8 text-accent text-2xl shadow-xl">
                                    <i className={item.icon}></i>
                                </div>
                                <h4 className="text-xl font-bold mb-4 uppercase tracking-tight text-white">{item.title}</h4>
                                <p className="text-white/70 text-sm font-medium leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Approach Section */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em] mb-4 block">The Process</span>
                        <h2 className="text-4xl font-black text-black tracking-tight mb-6">Smart & Structured <span className="text-accent italic">Guidance</span></h2>
                        <p className="text-black/60 text-lg font-medium leading-relaxed">We follow a systematic process to ensure smooth and successful transactions.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden xl:block absolute top-[44px] left-[10%] right-[10%] h-[1px] bg-gray-100 z-0"></div>
                        
                        {[
                            "Understand your requirement",
                            "Shortlist verified properties",
                            "Arrange site visits",
                            "Negotiate best price",
                            "Assist with legal documentation",
                            "Final deal closure"
                        ].map((step, i) => (
                            <motion.div 
                                key={i}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                className="relative z-10 text-center flex flex-col items-center group"
                            >
                                <div className="w-20 h-20 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-accent text-xl font-black mb-6 group-hover:bg-accent group-hover:text-white transition-all duration-500">
                                    {i + 1}
                                </div>
                                <p className="text-black text-sm font-black uppercase tracking-tight max-w-[140px] leading-tight">
                                    {step}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-24 p-12 rounded-3xl bg-gray-50 border border-gray-100 max-w-4xl mx-auto text-center">
                        <h3 className="text-2xl font-black text-black mb-6 italic tracking-tight">Client Commitment</h3>
                        <p className="text-black font-medium leading-relaxed max-w-2xl mx-auto">
                            Although newly launched, our commitment is strong. We prioritize trust, transparency, and long-term relationships over short-term gains.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Information Section */}
            <section className="py-24 bg-gray-50/50">
                <div className="container mx-auto px-4">
                    <div className="bg-white p-12 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-accent opacity-5 rounded-full blur-3xl -mr-32 -mt-32"></div>
                        <div className="flex flex-wrap items-center -mx-6">
                            <div className="lg:w-1/2 px-6 mb-12 lg:mb-0">
                                <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em] mb-4 block">Get In Touch</span>
                                <h2 className="text-4xl font-black text-black mb-8 tracking-tight uppercase leading-tight">My Property <span className="text-accent italic">Guru</span></h2>
                                <p className="text-black/60 text-lg font-medium leading-relaxed max-w-md">
                                    Professional real estate consultancy delivering premiums results in Ahmedabad and beyond.
                                </p>
                            </div>
                            <div className="lg:w-1/2 px-6">
                                <div className="space-y-6">
                                    <div className="flex items-center gap-6 p-6 rounded-2xl border border-gray-50 hover:bg-gray-50 transition-colors">
                                        <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                                            <i className="fa-solid fa-location-dot text-accent"></i>
                                        </div>
                                        <div>
                                            <p className="text-black/40 text-[10px] uppercase font-black tracking-widest mb-1">Office Location</p>
                                            <p className="text-black font-bold">Ahmedabad, Gujarat, India</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-6">
                                        <div className="flex-1 min-w-[240px] flex items-center gap-6 p-6 rounded-2xl border border-gray-50 hover:bg-gray-50 transition-colors">
                                            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                                                <i className="fa-solid fa-phone text-accent"></i>
                                            </div>
                                            <div>
                                                <p className="text-black/40 text-[10px] uppercase font-black tracking-widest mb-1">Call Us</p>
                                                <p className="text-black font-bold">+91 9537002666</p>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-[240px] flex items-center gap-6 p-6 rounded-2xl border border-gray-50 hover:bg-gray-50 transition-colors">
                                            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                                                <i className="fa-solid fa-envelope text-accent"></i>
                                            </div>
                                            <div>
                                                <p className="text-black/40 text-[10px] uppercase font-black tracking-widest mb-1">Email Inquiry</p>
                                                <p className="text-black font-bold text-sm">mypropertyguru.in@gmail.com</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            <Footer />
        </div>
    )
}
